import { channelIdForOwner, identityIdFromSigningKey, verifyInvite } from "./crypto.js";
import { MemberScheduleSchema, BroadcastMessageSchema, BroadcastManifestSchema, BroadcastReceiptSchema, PushWakeGrantSchema, SyncRequestSchema, } from "./schemas.js";
export function operationAllowedForRole(operation, authorizedRole) {
    // An invite proves that its issuer can act at least as a coach, but the
    // current invite format does not expose whether the issuer is an owner.
    // Accept owner-authored operations through that minimum coach capability;
    // the checks below still prevent owner-only actions such as revocation.
    const roleMatches = operation.authorRole === authorizedRole ||
        (authorizedRole === "coach" && operation.authorRole === "owner");
    if (!roleMatches)
        return false;
    if (operation.kind === "gym.updated")
        return authorizedRole === "coach" || authorizedRole === "owner";
    if (operation.kind === "schedule.updated") {
        const schedule = MemberScheduleSchema.safeParse(operation.payload);
        if (!schedule.success)
            return false;
        return (authorizedRole === "coach" ||
            authorizedRole === "owner" ||
            schedule.data.memberId === operation.authorId);
    }
    if (operation.kind === "role.issued")
        return authorizedRole === "coach" || authorizedRole === "owner";
    // La capienza la pubblica solo chi possiede tutte le pianificazioni: un atleta
    // potrebbe altrimenti dichiarare piena una giornata che non e' piena.
    if (operation.kind === "occupancy.published")
        return authorizedRole === "coach" || authorizedRole === "owner";
    if (operation.kind === "broadcast.published") {
        const message = BroadcastMessageSchema.safeParse(operation.payload);
        return (message.success &&
            message.data.createdBy === operation.authorId &&
            (authorizedRole === "coach" || authorizedRole === "owner"));
    }
    if (operation.kind === "broadcast.manifest") {
        const manifest = BroadcastManifestSchema.safeParse(operation.payload);
        return (manifest.success &&
            manifest.data.senderId === operation.authorId &&
            (authorizedRole === "coach" || authorizedRole === "owner"));
    }
    if (operation.kind === "broadcast.receipt") {
        const receipt = BroadcastReceiptSchema.safeParse(operation.payload);
        return (receipt.success && authorizedRole === "member" && receipt.data.memberId === operation.authorId);
    }
    if (operation.kind === "push.wake.granted") {
        const grant = PushWakeGrantSchema.safeParse(operation.payload);
        return (grant.success && grant.data.subjectId !== operation.authorId && authorizedRole === "owner");
    }
    if (operation.kind === "role.revoked")
        return authorizedRole === "owner";
    // Chi accoglie un nuovo arrivato e' chi puo' dire che il suo invito e' speso:
    // un atleta non vede entrare nessuno e non ha nulla da registrare.
    if (operation.kind === "invite.consumed")
        return authorizedRole === "coach" || authorizedRole === "owner";
    /**
     * Il rifiuto lo dice chi accoglie, cioe' chi tiene l'elenco degli inviti
     * spesi: un atleta non vede entrare nessuno e non avrebbe niente da
     * rifiutare. Chi lo riceve non ne ricava alcun potere — e' una notizia su
     * se stesso, non un titolo.
     */
    if (operation.kind === "invite.rejected")
        return authorizedRole === "coach" || authorizedRole === "owner";
    if (operation.kind === "conflict.resolved") {
        return authorizedRole === "coach" || authorizedRole === "owner";
    }
    if (operation.kind === "sync.requested") {
        const request = SyncRequestSchema.safeParse(operation.payload);
        return request.success && request.data.requesterId === operation.authorId;
    }
    return false;
}
export function resolveOperationAuthorization(input) {
    if (input.operation.gymId !== input.expectedGymId ||
        input.operation.authorId !== input.author.id ||
        identityIdFromSigningKey(input.author.signingPublicKey) !== input.author.id) {
        return undefined;
    }
    // Prima dei pari noti e prima degli inviti: una revoca che valesse solo per
    // gli sconosciuti non sarebbe una revoca.
    const revocations = (input.revokedPeers ?? []).filter((revocation) => revocation.gymId === input.expectedGymId && revocation.subjectId === input.author.id);
    /**
     * Una revoca chiude l'accesso, ma non mura la porta: la via di rientro del
     * prodotto e' «chiedi al coach un invito nuovo», e qui e' dove quell'invito
     * deve valere. Decide l'ordine dei tempi firmati: solo un invito emesso
     * *dopo* l'ultima revoca la supera. L'invito con cui il revocato era entrato
     * e' per forza piu' vecchio della propria revoca, quindi non riapre nulla —
     * che e' esattamente la garanzia di SEC-04.
     */
    const latestRevokedAt = revocations.reduce((latest, revocation) => Math.max(latest, Date.parse(revocation.revokedAt)), Number.NEGATIVE_INFINITY);
    const invitationSupersedesRevocation = input.invitation !== undefined &&
        Date.parse(input.invitation.payload.issuedAt) > latestRevokedAt;
    if (revocations.length > 0 && !invitationSupersedesRevocation)
        return undefined;
    const known = input.knownPeers.find((peer) => peer.id === input.author.id);
    // Un revocato rientra solo per la porta dell'invito nuovo, che va verificato
    // per intero: il vecchio posto fra i pari, se un dispositivo lo conserva
    // ancora, non vale piu'.
    if (known && revocations.length === 0) {
        const sameKeys = known.signingPublicKey === input.author.signingPublicKey &&
            known.encryptionPublicKey === input.author.encryptionPublicKey;
        return sameKeys && operationAllowedForRole(input.operation, known.role) ? known : undefined;
    }
    const invitation = input.invitation;
    if (!invitation)
        return undefined;
    // Un invito gia' speso vale ancora per chi l'ha speso — puo' presentarsi
    // prima che il suo certificato di ruolo sia arrivato — e per nessun altro.
    const spent = input.spentInvites?.find((entry) => entry.gymId === input.expectedGymId && entry.inviteId === invitation.payload.id);
    if (spent && spent.consumedBy !== input.author.id)
        return undefined;
    const issuer = input.knownPeers.find((peer) => peer.id === invitation.payload.issuedBy);
    /**
     * Invitare e' del solo proprietario, e il proprietario si riconosce dalla
     * chiave: il canale della palestra e' derivato dalla sua, quindi nessuno puo'
     * fingersi lui senza firmare con una chiave che non ha. Un coach che emette
     * inviti non fa entrare nessuno — il rifiuto e' qui, dalla parte di chi
     * riceve, dove un client modificato non arriva.
     */
    const issuerMayInvite = input.expectedChannelId
        ? channelIdForOwner(invitation.issuerSigningKey) === input.expectedChannelId
        : issuer?.role === "owner";
    const proofMatches = invitation.payload.gymId === input.expectedGymId &&
        /**
         * L'invito deve nominare *questo* canale. Senza, chi invita potrebbe
         * dichiarare un canale derivato dalla propria chiave — auto-ancorandosi — e
         * superare cosi' un controllo di proprieta' fatto sull'invito invece che
         * sulla palestra in cui l'invito pretende di far entrare.
         */
        (input.expectedChannelId === undefined ||
            invitation.payload.channelId === input.expectedChannelId) &&
        invitation.payload.role === input.operation.authorRole &&
        invitation.issuerSigningKey === issuer?.signingPublicKey &&
        verifyInvite(invitation, input.now ?? new Date().toISOString());
    if (!issuerMayInvite ||
        !proofMatches ||
        !operationAllowedForRole(input.operation, invitation.payload.role)) {
        return undefined;
    }
    return { ...input.author, role: invitation.payload.role };
}
